import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react'
import CaretDownIcon from '@tecuity/sos-icons/react/caret-down'
import DownloadIcon from '@tecuity/sos-icons/react/download-box'
import { SearchBox } from '@tecuity/granite-ui/SearchBox'
import NewCollapsibleTable from '../Table/NewCollapsibleTable'
import { useLocation } from 'react-router'
import FloatingMenu from 'components/FloatingMenu/FloatingMenu'
import { Spinner } from 'components/Loaders/Loaders'
import { Body } from 'components/Shell/Shell'
import * as api from '../../requests'
import { useDebounce } from '../../Utilities/hooks'
import './NewList.scss'
import '../WorkviewManager/WorkviewManager.scss'
import EmptySearchPlaceholder from '../Search/EmptySearchPlaceholder'

const NewList = () => {
  const location = useLocation()
  const [filterText, setFilterText] = useState('')
  const debouncedFilterText = useDebounce(filterText.toLowerCase(), 100)
  const [listDetails, setListDetails] = useState({})
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [selectedOption, setSelectedOption] = useState(null)
  const [dropdownOptionsOpen, setDropdownOptionsOpen] = useState(false)
  const [rows, setRows] = useState({})
  const [loadingRows, setLoadingRows] = useState(false)
  const optionRef = useRef(null)

  const category = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location.pathname])

  useEffect(() => {
    if (category) {
      setLoadingDetails(true)
      api.GET_LIST_DETAILS_BY_CATEGORY(
        category,
        (details) => {
          setLoadingDetails(false)
          setListDetails(details)
          setSelectedOption(details.DropdownOptions[0].Value)
        },
        (err) => {
          console.error(err)
          setListDetails({})
          setSelectedOption(null)
          setLoadingDetails(false)
        },
      )
    }
  }, [category])

  useEffect(() => {
    if (selectedOption && category) {
      setLoadingRows(true)
      api.GET_LIST_TABLE_DATA(
        category,
        selectedOption,
        (data) => {
          setRows(data)
          setLoadingRows(false)
        },
        (err) => {
          console.error(err)
          setRows({})
          setLoadingRows(false)
        },
      )
    }
  }, [category, selectedOption])

  const filteredRows = useMemo(() => {
    if (!debouncedFilterText) return rows || []

    const mainAccessors = (listDetails.MainTemplate || [])
      .filter((t) => t.IsEnabled)
      .map((t) => t.Accessor)

    const subAccessorsList = (listDetails.SubTemplates || []).map((st) => {
      const subAccessors = (st.Template || []).filter((t) => t.IsEnabled).map((t) => t.Accessor)
      return [st.ArrayAccessor, subAccessors]
    })

    return (rows || []).filter((r) => {
      if (
        mainAccessors.find((accessor) => r[accessor].toLowerCase().includes(debouncedFilterText))
      ) {
        return true
      }
      if (
        subAccessorsList.find(([arrayAccessor, subAccessors]) => {
          return r[arrayAccessor].find((subRow) => {
            return subAccessors.find((accessor) =>
              subRow[accessor].toLowerCase().includes(debouncedFilterText),
            )
          })
        })
      ) {
        return true
      }
      return false
    })
  }, [rows, listDetails, debouncedFilterText])

  return (
    <Body className="wvm-wrapper">
      {loadingDetails && (
        <div style={{ height: '100%', width: '100%', textAlign: 'center', padding: '4rem' }}>
          <Spinner className="page-loader" themeColor="dark-neutral" />
        </div>
      )}
      {!loadingDetails && (
        <div>
          <div className="main-wrapper space-between">
            <h1>
              <Fragment>
                {`${listDetails.Title}: `}
                <div
                  className="subtitle clickable"
                  ref={optionRef}
                  tabIndex={0}
                  onClick={() => setDropdownOptionsOpen(!dropdownOptionsOpen)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setDropdownOptionsOpen(!dropdownOptionsOpen)
                  }}
                >
                  {(
                    (listDetails.DropdownOptions || []).find(
                      (opt) => opt.Value === selectedOption,
                    ) || {}
                  ).Label || ''}
                  <CaretDownIcon />
                </div>
                <FloatingMenu
                  className="fm-icon-menu"
                  isOpen={dropdownOptionsOpen}
                  alignTo={optionRef.current}
                  align="right"
                  onRequestClose={() => setDropdownOptionsOpen(!dropdownOptionsOpen)}
                  options={(listDetails.DropdownOptions || []).map((opt) => ({
                    label: opt.Label,
                    value: opt.Value,
                    onClick: () => setSelectedOption(opt.Value),
                  }))}
                />
              </Fragment>
            </h1>
            <div className="wvm-search-wrapper">
              <SearchBox
                placeholder={listDetails.TextFilterPlaceholder || 'Filter results...'}
                value={filterText}
                onChange={(e) => setFilterText(e)}
                onClear={() => setFilterText('')}
                showClearButton={true}
                showSearchButton={false}
              />
            </div>
          </div>
          <div className="download-link">
            <a
              href={`${api.BASE_URL}/api/Lists/Results/${category}/${selectedOption}/Csv`}
              target="_blank"
              download
            >
              <DownloadIcon /> <span>Download as .csv</span>
            </a>
          </div>
        </div>
      )}

      {loadingRows && !loadingDetails && (
        <div style={{ display: 'flex', justifyContent: 'center'}}>
          <Spinner className="page-loader" themeColor="dark-neutral" size="small" />
        </div>
      )}
      {!loadingRows && rows.length > 0 && (
        <NewCollapsibleTable
          rows={filteredRows}
          mainTemplate={listDetails.MainTemplate || {}}
          subTemplates={listDetails.SubTemplates || []}
          expandable
        />
      )}
      {rows.length > 0 && filteredRows.length === 0 ? (
        <div>
          <EmptySearchPlaceholder searchTerm={debouncedFilterText} />
        </div>
      ) : null}
      {!loadingRows && rows.length === 0 ? (
        <h3 style={{ display: 'flex', justifySelf: 'center', fontSize: '24px' }}>
          Queried List has no data.
        </h3>
      ) : null}
    </Body>
  )
}

export default NewList
